import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, CheckCircle2, Heart, Lock, MessageCircle, Wind, Moon, Users, PenLine, TrendingUp, BookOpen } from 'lucide-react';
import VoiceInputButton from '@/components/VoiceInputButton';
import Slider1to10 from '@/components/Slider1to10';

const MOODS = ['low', 'okay', 'steady', 'good', 'strong'];
const STRESSORS = ['Work', 'Family', 'Relationships', 'Money', 'Health', 'Other'];
const SLEEP = ['<5h', '5-6h', '6-7h', '7-8h', '8h+'];

const STEPS = [
  { key: 'mood',        title: 'How are you feeling today?',       kind: 'mood' },
  { key: 'stress',      title: 'How stressed do you feel?',        kind: 'slider', low: 'Very calm',        high: 'Very overwhelmed', invert: true },
  { key: 'anxiety',     title: 'How anxious do you feel?',         kind: 'slider', low: 'Not anxious',       high: 'Very anxious',     invert: true },
  { key: 'energy',      title: 'What\'s your energy level?',        kind: 'slider', low: 'Drained',           high: 'Energized' },
  { key: 'sleep',       title: 'How was last night\'s sleep?',      kind: 'sleep' },
  { key: 'hopefulness', title: 'How hopeful do you feel?',         kind: 'slider', low: 'Hopeless',          high: 'Very hopeful' },
  { key: 'social',      title: 'How connected did you feel today?',kind: 'slider', low: 'Isolated',          high: 'Very connected' },
  { key: 'confidence',  title: 'How confident do you feel today?', kind: 'slider', low: 'Shaky',             high: 'Very confident' },
  { key: 'stressor',    title: 'What\'s weighing on you most?',     kind: 'stressor' },
  { key: 'note',        title: 'Anything you want to share?',      kind: 'note' },
];

function LockedScreen({ nextAt }) {
  const nav = useNavigate();
  const [remaining, setRemaining] = useState(() => nextAt ? new Date(nextAt).getTime() - Date.now() : 0);

  useEffect(() => {
    if (!nextAt) return;
    const target = new Date(nextAt).getTime();
    const t = setInterval(() => setRemaining(target - Date.now()), 1000);
    return () => clearInterval(t);
  }, [nextAt]);

  const ms = Math.max(0, remaining);
  const totalSec = Math.floor(ms / 1000);
  const hh = String(Math.floor(totalSec / 3600)).padStart(2, '0');
  const mm = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
  const ss = String(totalSec % 60).padStart(2, '0');

  return (
    <div className="min-h-screen bg-[#F9F8F6] p-6 lg:p-10 flex items-center" data-testid="checkin-locked">
      <div className="max-w-md lg:max-w-lg mx-auto w-full">
        <button onClick={()=>nav('/app')} data-testid="checkin-locked-back" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-6">
          <ArrowLeft size={14}/> Back to home
        </button>
        <div className="bg-white rounded-3xl p-8 lg:p-10 card-soft text-center">
          <div className="w-16 h-16 lg:w-20 lg:h-20 mx-auto rounded-full bg-beige flex items-center justify-center mb-5">
            <Lock size={28} className="text-navy/70"/>
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">One check-in a day</p>
          <h1 className="font-heading text-2xl lg:text-3xl leading-snug mt-3">You already checked in today.</h1>
          <p className="text-navy/60 text-sm lg:text-base mt-3 leading-relaxed">
            Consistency &gt; intensity. Come back tomorrow for your next reflection — small acts, real momentum.
          </p>

          <div className="bg-beige rounded-2xl p-5 mt-6" data-testid="checkin-countdown">
            <p className="text-xs uppercase tracking-widest text-navy/50 font-semibold">Next check-in unlocks in</p>
            <p className="font-heading text-3xl lg:text-4xl mt-2 tabular-nums" data-testid="checkin-countdown-value">
              {hh}<span className="text-navy/30">:</span>{mm}<span className="text-navy/30">:</span>{ss}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-6">
            <button onClick={()=>nav('/app/journal')} data-testid="checkin-locked-journal"
              className="bg-beige rounded-2xl px-4 py-3 text-sm font-medium hover:bg-[#EAE5D9]">
              Journal instead
            </button>
            <button onClick={()=>nav('/app/progress')} data-testid="checkin-locked-progress"
              className="bg-navy text-white rounded-2xl px-4 py-3 text-sm font-medium hover:bg-navy-soft">
              See progress
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const REC_ICONS = { message: MessageCircle, wind: Wind, moon: Moon, users: Users, pen: PenLine, trending: TrendingUp, book: BookOpen };

function RecommendationsScreen({ items, nav }) {
  return (
    <div className="min-h-screen bg-[#F9F8F6] p-6 lg:p-10 flex flex-col" data-testid="checkin-recommendations">
      <div className="max-w-2xl mx-auto w-full">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-gold/15 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={26} className="text-gold" />
          </div>
          <h1 className="font-heading text-3xl lg:text-4xl">Check-in saved</h1>
          <p className="text-navy/60 mt-2">Based on how today landed, here's where to put your energy next.</p>
        </div>

        <div className="space-y-3">
          {items.map((r) => {
            const Icon = REC_ICONS[r.icon] || Heart;
            return (
              <button key={r.id} onClick={() => nav(r.link)} data-testid={`rec-${r.id}`}
                className="w-full text-left bg-white rounded-2xl p-5 card-soft hover:shadow-md transition-smooth flex items-start gap-4 group">
                <span className="w-10 h-10 rounded-xl bg-navy text-gold flex items-center justify-center shrink-0"><Icon size={18} /></span>
                <span className="flex-1 min-w-0">
                  <span className="block font-heading text-lg text-navy">{r.title}</span>
                  <span className="block text-sm text-navy/60 mt-0.5 leading-relaxed">{r.body}</span>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-gold mt-2 group-hover:gap-2 transition-all">{r.cta} <ArrowRight size={14} /></span>
                </span>
              </button>
            );
          })}
        </div>

        <button onClick={() => nav('/app/progress')} data-testid="rec-progress"
          className="w-full mt-6 bg-beige text-navy rounded-2xl py-4 font-medium hover:bg-[#EAE5D9] transition-smooth">
          See your progress
        </button>
      </div>
    </div>
  );
}

export default function DailyCheckin() {
  const nav = useNavigate();
  const [i, setI] = useState(0);
  const [values, setValues] = useState({ energy: 3 });
  const [loading, setLoading] = useState(false);
  const [statusChecked, setStatusChecked] = useState(false);
  const [locked, setLocked] = useState(null); // { next_available_at }
  const [recs, setRecs] = useState(null); // recommendations shown after a successful check-in

  useEffect(() => {
    let cancelled = false;
    http.get('/checkin/status').then(r => {
      if (cancelled) return;
      if (r.data && r.data.can_checkin === false) {
        setLocked({ next_available_at: r.data.next_available_at });
      }
    }).catch(()=>{}).finally(() => { if (!cancelled) setStatusChecked(true); });
    return () => { cancelled = true; };
  }, []);

  const step = STEPS[i];
  const isLast = i === STEPS.length - 1;
  const progress = ((i + 1) / STEPS.length) * 100;

  const setVal = (key, v) => setValues(prev => ({ ...prev, [key]: v }));

  const canAdvance = () => {
    if (step.kind === 'note') return true;
    if (step.kind === 'stressor') return true;
    return values[step.key] !== undefined && values[step.key] !== null;
  };

  const goNext = () => {
    if (!canAdvance()) return;
    if (isLast) submit();
    else setI(i + 1);
  };
  const goBack = () => { if (i === 0) nav('/app'); else setI(i - 1); };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        mood: values.mood || 'steady',
        sleep: values.sleep || '6-7h',
        energy: values.energy ?? 3,
        stressor: values.stressor || null,
        note: values.note || null,
        stress: values.stress ?? null,
        anxiety: values.anxiety ?? null,
        hopefulness: values.hopefulness ?? null,
        social: values.social ?? null,
        confidence: values.confidence ?? null,
      };
      const r = await http.post('/checkins', payload);
      toast.success('Check-in saved. Nice work.');
      setRecs(r.data?.recommendations || []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && (detail?.error === 'checkin_locked' || typeof detail === 'object')) {
        setLocked({ next_available_at: detail?.next_available_at });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not save');
      }
    } finally { setLoading(false); }
  };

  if (!statusChecked) {
    return <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center text-navy/60" data-testid="checkin-loading">Loading…</div>;
  }

  if (locked) {
    return <LockedScreen nextAt={locked.next_available_at}/>;
  }

  if (recs) {
    return <RecommendationsScreen items={recs} nav={nav} />;
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6] p-6 lg:p-10 flex flex-col" data-testid="checkin-page">
      <div className="max-w-2xl lg:max-w-3xl mx-auto w-full flex-1 flex flex-col">
        <button onClick={goBack} data-testid="checkin-back" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-4 self-start">
          <ArrowLeft size={14}/> {i === 0 ? 'Back' : 'Previous'}
        </button>

        <div className="mb-4 lg:mb-6 flex items-center justify-between text-xs lg:text-sm text-navy/60">
          <span className="inline-flex items-center gap-2"><Heart size={14} className="text-gold"/> Daily Check-In</span>
          <span data-testid="checkin-step-indicator">{i + 1} of {STEPS.length}</span>
        </div>
        <div className="h-1 bg-navy/10 rounded-full overflow-hidden mb-8 lg:mb-10">
          <div className="h-full bg-gold transition-all" style={{ width: `${progress}%` }} data-testid="checkin-progress-bar"/>
        </div>

        <div className="bg-white rounded-3xl p-6 lg:p-10 card-soft flex-1" data-testid={`step-${step.key}`}>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-2">In the past 24 hours</p>
          <h2 className="font-heading text-2xl lg:text-3xl leading-snug mb-6 lg:mb-8">{step.title}</h2>

          {step.kind === 'mood' && (
            <div className="grid grid-cols-5 gap-2 lg:gap-3">
              {MOODS.map(m => (
                <button key={m} onClick={()=>setVal('mood', m)} data-testid={`mood-${m}`}
                  className={`h-14 lg:h-16 rounded-2xl transition-smooth text-xs lg:text-sm font-medium ${values.mood===m ? 'bg-navy text-white' : 'bg-beige hover:bg-[#EAE5D9]'}`}>{m}</button>
              ))}
            </div>
          )}

          {step.kind === 'slider' && (
            <Slider1to10
              value={values[step.key] ?? null}
              onChange={(v)=>setVal(step.key, v)}
              lowLabel={step.low}
              highLabel={step.high}
              testid={`slider-${step.key}`}
              invertColor={step.invert}
            />
          )}

          {step.kind === 'sleep' && (
            <div className="grid grid-cols-5 gap-2 lg:gap-3">
              {SLEEP.map(s => (
                <button key={s} onClick={()=>setVal('sleep', s)} data-testid={`sleep-${s}`}
                  className={`h-14 lg:h-16 rounded-2xl text-sm lg:text-base font-medium ${values.sleep===s ? 'bg-navy text-white' : 'bg-beige hover:bg-[#EAE5D9]'}`}>{s}</button>
              ))}
            </div>
          )}

          {step.kind === 'stressor' && (
            <div className="flex flex-wrap gap-2 lg:gap-3">
              {STRESSORS.map(s => (
                <button key={s} onClick={()=>setVal('stressor', s)} data-testid={`stressor-${s}`}
                  className={`text-sm lg:text-base px-4 py-3 rounded-full ${values.stressor===s ? 'bg-navy text-white' : 'bg-beige hover:bg-[#EAE5D9]'}`}>{s}</button>
              ))}
            </div>
          )}

          {step.kind === 'note' && (
            <div className="relative">
              <textarea data-testid="checkin-note" value={values.note || ''} onChange={e=>setVal('note', e.target.value)}
                rows={5} placeholder="Anything you want to remember about today (optional)…"
                className="w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl p-4 pr-14 outline-none focus:ring-2 focus:ring-gold text-sm lg:text-base" />
              <div className="absolute right-3 bottom-3">
                <VoiceInputButton compact testid="checkin-note-voice-btn"
                  onTranscript={(chunk) => setVal('note', ((values.note || '') + ' ' + chunk).trim())}/>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-6 lg:mt-8 gap-3">
          <button onClick={goBack} data-testid="checkin-prev"
            className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy">
            <ArrowLeft size={14}/> Previous
          </button>
          <button
            onClick={goNext}
            disabled={!canAdvance() || loading}
            data-testid="checkin-next"
            className="inline-flex items-center gap-2 bg-navy text-white rounded-full px-6 py-3 hover:bg-navy-soft disabled:opacity-40">
            {isLast ? (loading ? 'Saving…' : (<>Save check-in <CheckCircle2 size={14}/></>)) : (<>Next <ArrowRight size={14}/></>)}
          </button>
        </div>
      </div>
    </div>
  );
}
